
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLanguage } from "../Context/LanguageContext";
import "./GalleryAlbum.css";

const EVENTS_API_URL =
    "https://script.google.com/macros/s/AKfycbwoNQHZnU5j4Pb8NIwzuVsBg5TBKlKRhYJXsxmCK0J6Rb8QRCf6-ixVudn7-KReaDcu/exec";

const IMAGE_BASE_PATH = "/images/events";

const FacebookIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
    >
        <path d="M14 8h3V4h-3c-3 0-5 2-5 5v2H6v4h3v7h4v-7h3l1-4h-4V9c0-.7.3-1 1-1Z" />
    </svg>
);

function GalleryAlbum() {
    const { eventId } = useParams();
    const { language } = useLanguage();
    const isBn = language === "bn";

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeImage, setActiveImage] = useState(null);

    /* =========================================
       LOAD EVENT
    ========================================= */

    useEffect(() => {
        const controller = new AbortController();

        async function loadEvent() {
            try {
                setLoading(true);
                setError("");
                setEvent(null);
                setActiveImage(null);

                const response = await fetch(
                    `${EVENTS_API_URL}?id=${encodeURIComponent(eventId)}`,
                    { signal: controller.signal }
                );

                if (!response.ok) {
                    throw new Error("Unable to load album.");
                }

                const data = await response.json();

                if (!data.success || !data.event) {
                    throw new Error(
                        data.error || "Album not found."
                    );
                }

                setEvent(data.event);
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error(err);
                    setError(err.message);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        loadEvent();

        return () => controller.abort();
    }, [eventId]);

    /* =========================================
       IMAGES
    ========================================= */

    const images = event
        ? String(event.gallery_images || "")
            .split(",")
            .map((image) => image.trim())
            .filter(Boolean)
            .map((image) => {
                if (/^https?:\/\//i.test(image)) {
                    return image;
                }

                if (image.startsWith("/")) {
                    return image;
                }

                return `${IMAGE_BASE_PATH}/${encodeURIComponent(
                    event.id
                )}/${image}`;
            })
        : [];

    const title = event
        ? (
            isBn
                ? event.title_bn || event.title_en
                : event.title_en || event.title_bn
        )
        : "";

    /* =========================================
       LIGHTBOX CONTROLS
    ========================================= */

    const closeLightbox = () => {
        setActiveImage(null);
    };

    const showPrevious = () => {
        setActiveImage((current) =>
            current === null
                ? null
                : (current - 1 + images.length) % images.length
        );
    };

    const showNext = () => {
        setActiveImage((current) =>
            current === null
                ? null
                : (current + 1) % images.length
        );
    };

    /* =========================================
       KEYBOARD NAVIGATION
    ========================================= */

    useEffect(() => {
        if (activeImage === null || images.length === 0) {
            return;
        }

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setActiveImage(null);
            }

            if (e.key === "ArrowLeft") {
                setActiveImage((current) =>
                    (current - 1 + images.length) % images.length
                );
            }

            if (e.key === "ArrowRight") {
                setActiveImage((current) =>
                    (current + 1) % images.length
                );
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };
    }, [activeImage, images.length]);

    /* =========================================
       RENDER
    ========================================= */

    return (
        <main className="gallery-album-page">

            {/* HERO */}

            <section className="gallery-album-hero">
                <div className="container gallery-album-hero-content">

                    <Link
                        to="/gallery"
                        className="gallery-album-back"
                    >
                        ← {isBn ? "সব অ্যালবাম" : "All Albums"}
                    </Link>

                    <span className="gallery-album-hero-label">
                        {isBn ? "ছবির অ্যালবাম" : "PHOTO ALBUM"}
                    </span>

                    <h1>
                        {loading
                            ? (isBn ? "লোড হচ্ছে..." : "Loading...")
                            : title || (isBn ? "অ্যালবাম" : "Album")}
                    </h1>

                    {!loading && !error && event && (
                        <p>
                            {images.length}{" "}
                            {isBn
                                ? "টি ছবি"
                                : images.length === 1
                                    ? "Photo"
                                    : "Photos"}
                        </p>
                    )}

                </div>
            </section>

            {/* CONTENT */}

            <section className="gallery-album-section">
                <div className="container">

                    {loading && (
                        <div className="gallery-album-status">
                            {isBn
                                ? "ছবিগুলো লোড হচ্ছে..."
                                : "Loading album..."}
                        </div>
                    )}

                    {!loading && error && (
                        <div className="gallery-album-status">
                            <h2>
                                {isBn
                                    ? "অ্যালবাম পাওয়া যায়নি"
                                    : "Album unavailable"}
                            </h2>
                            <p>{error}</p>
                            <Link to="/gallery">
                                {isBn
                                    ? "গ্যালারিতে ফিরে যান"
                                    : "Back to Gallery"}
                            </Link>
                        </div>
                    )}

                    {!loading && !error && event && (
                        <>
                            <div className="gallery-album-heading">
                                <div>
                                    <span>
                                        {isBn
                                            ? "আমাদের স্মৃতি"
                                            : "OUR MEMORIES"}
                                    </span>

                                    <h2>
                                        {isBn
                                            ? "অনুষ্ঠানের ছবি"
                                            : "Event Photos"}
                                    </h2>
                                </div>

                                <p>
                                    {isBn
                                        ? "বড় করে দেখতে ছবিতে ক্লিক করুন।"
                                        : "Click a photo to view it full screen."}
                                </p>
                            </div>

                            {images.length === 0 ? (
                                <div className="gallery-album-status">
                                    {isBn
                                        ? "এই অ্যালবামে এখনো কোনো ছবি নেই।"
                                        : "No photos have been added to this album yet."}
                                </div>
                            ) : (
                                <div className="gallery-photos-grid">
                                    {images.map((image, index) => (
                                        <button
                                            key={`${image}-${index}`}
                                            type="button"
                                            className="gallery-photo"
                                            onClick={() =>
                                                setActiveImage(index)
                                            }
                                            aria-label={
                                                isBn
                                                    ? `ছবি ${index + 1} দেখুন`
                                                    : `View photo ${index + 1}`
                                            }
                                        >
                                            <img
                                                src={image}
                                                alt={`${title} - ${index + 1}`}
                                                loading="lazy"
                                            />

                                            <span className="gallery-photo-overlay">
                                                <span>↗</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                </div>
            </section>

            {/* FULL-SCREEN LIGHTBOX */}

            {activeImage !== null && images.length > 0 && (
                <div
                    className="gallery-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={isBn ? "ছবি দেখুন" : "Photo viewer"}
                    onClick={closeLightbox}
                >
                    <button
                        type="button"
                        className="gallery-lightbox-close"
                        onClick={closeLightbox}
                        aria-label="Close"
                    >
                        ×
                    </button>

                    <button
                        type="button"
                        className="gallery-lightbox-prev"
                        onClick={(e) => {
                            e.stopPropagation();
                            showPrevious();
                        }}
                        aria-label="Previous photo"
                    >
                        ‹
                    </button>

                    <img
                        src={images[activeImage]}
                        alt={`${title} - ${activeImage + 1}`}
                        onClick={(e) => e.stopPropagation()}
                    />

                    <button
                        type="button"
                        className="gallery-lightbox-next"
                        onClick={(e) => {
                            e.stopPropagation();
                            showNext();
                        }}
                        aria-label="Next photo"
                    >
                        ›
                    </button>

                    <div className="gallery-lightbox-counter">
                        {activeImage + 1} / {images.length}
                    </div>
                </div>
            )}

            {/* ==================================================
                FACEBOOK PHOTOS
            ================================================== */}

            {event?.facebook_url && (

                <section className="event-facebook-section">

                    <div className="container">

                        <div className="event-facebook-box">


                            {/* FACEBOOK ICON */}

                            <div className="event-facebook-icon">
                                <FacebookIcon />
                            </div>


                            {/* DIVIDER */}

                            <div className="event-facebook-divider"></div>


                            {/* TEXT */}

                            <div className="event-facebook-content">

                                <h2>
                                    {language === "bn"
                                        ? "আরও ছবি দেখতে চান?"
                                        : "Want to see more photos?"}
                                </h2>

                                <p>
                                    {language === "bn"
                                        ? "এই অনুষ্ঠানের সম্পূর্ণ ছবির অ্যালবাম আমাদের Facebook পেজে দেখুন।"
                                        : "See the complete photo album from this event on our Facebook page."}
                                </p>

                            </div>


                            {/* FACEBOOK BUTTON */}

                            <a
                                href={event.facebook_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="event-facebook-button"
                            >
                                <span className="event-facebook-external-icon">
                                    ↗
                                </span>

                                <span>
                                    {language === "bn"
                                        ? "Facebook-এ সব ছবি দেখুন"
                                        : "View all photos on Facebook"}
                                </span>

                                <span className="event-facebook-arrow">
                                    →
                                </span>
                            </a>


                        </div>

                    </div>

                </section>

            )}


        </main>
    );
}

export default GalleryAlbum;
